import React, { useState, useEffect } from "react";
import { useSolution } from "../../../../contexts/SolutionsContext";
import IconAndText from "../../../Atoms/IconAndText";
import { CircularProgress } from "@mui/material";
import Seperator from "../../../Atoms/Seperator";
import CustomDropdown from "../../../Atoms/DropDowns/CustomDropdown";
import MultiSelectDropdown from "../../../Atoms/DropDowns/MultiSelectDropdown";
import IconAndTextButton from "../../../Atoms/Buttons/IconAndTextButton";
import crossIcon from "../../../../assets/Icon-area-16.svg";

const DefineSolClass = ({ sno = 4, type = "register" }) => {
  const {
    ongoingSolutionData,
    updateOngoingSolDataL3,
    isLoadingDropdownFields,
    lookUpValues,
    fetchSolutionSectorsSilently,
    isInitializingEdit,
  } = useSolution();

  const [industrySectors, setIndustrySectors] = useState([
    { industry: "", sector: [], sectorValues: [] },
    { industry: "", sector: [], sectorValues: [] },
    { industry: "", sector: [], sectorValues: [] },
  ]);

  const [isSectorValuesFetching, setIsSectorValuesFetching] = useState([
    { index: null, isFetching: false },
  ]);

  const handleIndustryChange = async (index, value) => {
    setIsSectorValuesFetching((prev) => {
      const filtered = prev.filter((item) => item.index !== index);
      return [...filtered, { index, isFetching: true }];
    });

    try {
      const sectorsFromAPI = await fetchSolutionSectorsSilently(value);

      const updatedSectors = industrySectors.map((item, i) =>
        i === index
          ? { industry: value, sectorValues: sectorsFromAPI, sector: [] }
          : item
      );

      setIndustrySectors(updatedSectors);
    } finally {
      setIsSectorValuesFetching((prev) => {
        const filtered = prev.filter((item) => item.index !== index);
        return [...filtered, { index, isFetching: false }];
      });
    }
  };

  const handleSectorChange = (index, value) => {
    const updatedSectors = industrySectors.map((item, i) =>
      i === index ? { ...item, sector: value } : item
    );

    setIndustrySectors(updatedSectors);
    updateOngoingSolDataL3("industry_info", updatedSectors);
  };

  const handleRemoveIndustry = (indexToRemove) => {
    if (industrySectors?.length > 1) {
      const updatedSectors = industrySectors.filter(
        (_, index) => index !== indexToRemove
      );

      setIndustrySectors(updatedSectors);
      updateOngoingSolDataL3("industry_info", updatedSectors);
    }
  };

  const handleAddIndustry = () => {
    const newBlankRow = { industry: "", sector: [], sectorValues: [] };
    const updatedSectors = [...industrySectors, newBlankRow];

    setIndustrySectors(updatedSectors);
  };

  useEffect(() => {
    if (type === "edit") {
      setIndustrySectors(
        ongoingSolutionData?.classifications?.[0]?.industry_info || []
      );
    }
  }, [ongoingSolutionData?.classifications, type]);

  return (
    <article className="define-sol-classification">
      {/* Headers */}
      <section>
        <div id="sol-classification-id" className="mb-2">
          <IconAndText
            text="Define Classification"
            clsNames="mas-icon-txt mb-2"
            iconBefore={<div>{sno}</div>}
          />
        </div>
        <div className="d-flex align-items-center">
          <div className="def-sol-cls-sub-hdr mt-4 mb-3">
            Industry and Sector Information
          </div>

          {(isLoadingDropdownFields?.industries || isInitializingEdit) && (
            <CircularProgress
              style={{
                width: "15px",
                height: "15px",
                opacity: 0.8,
                marginLeft: "5px",
              }}
            />
          )}
        </div>
      </section>

      {/* Industry Sectors Dropdowns */}
      <section
        className="mas-sol-industries-sector"
        style={
          isLoadingDropdownFields?.industries || isInitializingEdit
            ? { opacity: 0.3, pointerEvents: "none" }
            : {}
        }
      >
        <div className="d-flex  gp-x-16 industry-sector-row">
          <div className="wd-4 fw-bold">#</div>
          <div className="wd-36 fw-bold">Select Industry</div>
          <div className="wd-58 fw-bold">Select Sector</div>
          <div className="wd-2"></div>
        </div>

        <Seperator
          clsNames={"mas-horizontal-seperator-100"}
          styles={{
            background: "var(--db-secondary-gray-six)",
            margin: "8px 0",
            opacity: 0.8,
          }}
        />

        {industrySectors?.map((item, index) => {
          return (
            <>
              <div key={index} className="d-flex industry-sector-row  gp-x-16">
                <div className="wd-4 d-flex align-items-center">
                  <span className="mas-numbering">
                    <strong>{index + 1}</strong>
                  </span>
                </div>

                <div className="wd-36 pd">
                  <CustomDropdown
                    selectValues={
                      lookUpValues?.industries?.map((industry) => ({
                        value: industry,
                        label: industry,
                      })) || []
                    }
                    value={item?.industry || ""}
                    handleOnChange={(value) =>
                      handleIndustryChange(index, value)
                    }
                    dropdownLabel={"Select/Type to Search"}
                    clsNames={"mas-multiselect-dd-primary mas-cust-dd-2"}
                  />
                </div>

                <div className="wd-58 pd">
                  <MultiSelectDropdown
                    selectValues={
                      item?.sectorValues?.map((sector) => ({
                        value: sector,
                        label: sector,
                      })) || []
                    }
                    values={item?.sector || []}
                    handleOnChange={(values) =>
                      handleSectorChange(index, values)
                    }
                    dropdownLabel="Select/Type to Search"
                    clsNames="mas-multiselect-dd-primary mas-multi-dd-2"
                    disabled={
                      !item?.sectorValues?.length ||
                      isSectorValuesFetching.some(
                        (fetchItem) =>
                          fetchItem.index === index && fetchItem.isFetching
                      )
                    }
                  />
                </div>

                <div className="wd-2 d-flex align-items-center justify-content-end">
                  {isSectorValuesFetching.find(
                    (item) => item.index === index && item.isFetching
                  ) ? (
                    <CircularProgress
                      style={{
                        width: "16px",
                        height: "16px",
                        opacity: 0.8,
                      }}
                    />
                  ) : (
                    <IconAndTextButton
                      icon={<img src={crossIcon} alt="Remove" />}
                      text=""
                      iconPos="right"
                      iconStyles={{
                        width: 16,
                        height: 16,
                        opacity: industrySectors.length === 1 ? 0.3 : 1,
                        cursor:
                          industrySectors.length === 1
                            ? "not-allowed"
                            : "pointer",
                      }}
                      clsNames="mas-cross-icon-btn"
                      onClickFn={() => {
                        if (industrySectors.length > 1) {
                          handleRemoveIndustry(index);
                        }
                      }}
                      disabled={industrySectors.length === 1}
                    />
                  )}
                </div>
              </div>
              <Seperator
                clsNames={"mas-horizontal-seperator-100"}
                styles={{
                  background: "var(--db-secondary-gray-six)",
                  margin: "8px 0",
                  opacity: 0.8,
                }}
              />
            </>
          );
        })}
      </section>

      {/* Add Industry Button */}
      <div className="add-btn mt-2">
        <IconAndTextButton
          icon={<span style={{ fontSize: 20 }}>+</span>}
          text="Add Industry"
          iconPos="left"
          iconStyles={{ fontSize: 20, marginRight: 4 }}
          clsNames="mas-plus-icon-btn mb-2"
          onClickFn={handleAddIndustry}
        />
      </div>

      {/* Applicable Business Function Dropdown */}
      <section className="wd-50 mb-2">
        <label className="mas-form-label-regular mb-2">
          {"Applicable Business Function"}
        </label>
        <div className="d-flex align-items-center">
          <MultiSelectDropdown
            selectValues={
              lookUpValues?.bFs?.map((item) => {
                return { value: item, label: item };
              }) || []
            }
            values={
              ongoingSolutionData?.classifications?.[0]?.business_function || []
            }
            handleOnChange={(selectedValues) => {
              updateOngoingSolDataL3("business_function", selectedValues);
            }}
            placement={"top"}
            dropdownLabel="Select/Type to Search"
            clsNames="mas-multiselect-dd-primary mas-multi-dd-2"
            disabled={isLoadingDropdownFields?.bFs}
          />
          {isLoadingDropdownFields?.bFs && (
            <CircularProgress
              style={{
                width: "15px",
                height: "15px",
                opacity: 0.8,
                marginLeft: "5px",
              }}
            />
          )}
        </div>
      </section>
    </article>
  );
};

export default DefineSolClass;
